import { useEffect, useState } from "react";

import { useAnalytics, useAnalyticsAttributes } from "../../components";
import { UiVideoPlayerModel } from "../../models";

import {
    VideoRatio16By9,
    ResponsiveVideoWrapper,
    ResponsiveVideoFrame,
    VideoEmbed
} from "./UiVideoPlayer.styles";

export type UiVideoPlayerProps = {
    model: UiVideoPlayerModel;
};

type WistiaVideo = {
    bind: (event: string, callback: () => void) => void;
};

type WistiaVideoConfigItem = {
    id: string;
    onReady: (video: WistiaVideo) => void;
};

type RevokeWistiaConfigItem = {
    revoke: WistiaVideoConfigItem;
};

declare global {
    interface Window {
        _wq?: Array<WistiaVideoConfigItem | RevokeWistiaConfigItem>;
    }
}

const UiVideoPlayer = ({ model }: UiVideoPlayerProps) => {
    const [embed, setEmbed] = useState<JSX.Element | null>(null);
    const { sendTrackEvent } = useAnalytics();
    const analyticsAttributes = useAnalyticsAttributes();

    useEffect(() => {
        switch (model.content.videoProvider) {
            case "Loom":
                setEmbed(
                    <VideoRatio16By9 data-testid="loom-video">
                        <ResponsiveVideoFrame
                            src={`https://www.loom.com/embed/${model.content.videoId}`}
                            frameBorder="0"
                            allowFullScreen={true}
                            className="loom_embed"
                            title={model.content.title}
                        />
                    </VideoRatio16By9>
                );
                return;
            case "Wistia":
                const wistiaJsonp = document.createElement("script");
                wistiaJsonp.src = `//fast.wistia.com/embed/medias/${model.content.videoId}.jsonp`;
                wistiaJsonp.dataset["testid"] = "wistia-jsonp";
                wistiaJsonp.async = true;

                const wistia = document.createElement("script");
                wistia.src = `//fast.wistia.com/assets/external/E-v1.js`;
                wistia.dataset["testid"] = "wistia";
                wistia.async = true;

                document.body.append(wistiaJsonp, wistia);

                const wistiaEmbedInitConfig = {
                    id: model.content.videoId,
                    onReady: (video: WistiaVideo) => {
                        /* istanbul ignore next */
                        video.bind("play", () => {
                            sendTrackEvent("playedVideo", {
                                ...analyticsAttributes,
                                event: "played",
                                eventComponent: model.type,
                                label: model.content.title,
                                labelEnglish: model.content.entryTitle,
                                uuid: model.id,
                                title: model.content.title
                            });
                        });
                    }
                };

                /* istanbul ignore next */
                window._wq = window._wq || [];
                window._wq.push(wistiaEmbedInitConfig);

                setEmbed(
                    <VideoRatio16By9 className="wistia_responsive_padding">
                        <ResponsiveVideoWrapper className="wistia_responsive_wrapper">
                            <VideoEmbed
                                className={`wistia_embed wistia_async_${model.content.videoId} seo=false videoFoam=true`}
                            />
                        </ResponsiveVideoWrapper>
                    </VideoRatio16By9>
                );

                return () => {
                    document.body.removeChild(wistiaJsonp);
                    document.body.removeChild(wistia);

                    /* istanbul ignore next */
                    window._wq = window._wq || [];
                    window._wq.push({ revoke: wistiaEmbedInitConfig });
                };
            default:
                setEmbed(
                    <div data-testid="unsupported-video-provider">
                        Unsupported video provider
                    </div>
                );
                return;
        }
    }, [analyticsAttributes, model, sendTrackEvent]);

    return (
        <div data-testid="ui-video-player">
            {embed ?? (
                <VideoRatio16By9
                    aria-hidden="true"
                    data-testid="video-placeholder"
                />
            )}
        </div>
    );
};

export default UiVideoPlayer;
